import {
  LAYOUT_ANIMATION_DURATION,
  LAYOUT_ANIMATION_EASING,
  LAYOUT_FLIP_NODE_SELECTOR,
} from './constants';
import { isFinitePosition } from './elkPositionHelpers';
import { isBitmapSourceNode } from '../../cardBitmaps/findTaskElement';

/**
 * Animate task-node elements from current DOM position to ELK target positions
 * using a direct-DOM FLIP approach that bypasses React rendering entirely.
 *
 * 1. Read each node's current transform from the DOM (the "First" snapshot).
 * 2. Write the transition rule + target transform directly onto each element.
 * 3. The browser interpolates old→new = smooth slide.
 * 4. After the transition ends, commit React state so React catches up.
 */
export function animateNodesToPositions(
  newPositions,
  onComplete,
  durationMs = LAYOUT_ANIMATION_DURATION,
  onAfterInlineCleanup = null,
) {
  const TRANSITION = `transform ${durationMs}ms ${LAYOUT_ANIMATION_EASING}`;
  const nodes = document.querySelectorAll(LAYOUT_FLIP_NODE_SELECTOR);

  nodes.forEach((el) => {
    if (isBitmapSourceNode(el)) return;
    const id = el.getAttribute('data-task-id');
    const target = newPositions[id];
    if (!target) return;

    // Write transition + target transform directly onto the DOM element.
    // The browser already knows the current computed transform from the previous paint,
    // so adding a transition and changing the value in the same JS task causes interpolation.
    el.style.transition = TRANSITION;
    el.style.transform = `translate(${target.x}px, ${target.y}px)`;
  });

  // After the CSS transition finishes, clean up inline styles and let React take over.
  setTimeout(() => {
    document.querySelectorAll(LAYOUT_FLIP_NODE_SELECTOR).forEach((el) => {
      if (isBitmapSourceNode(el)) return;
      const id = el.getAttribute('data-task-id');
      const target = newPositions[id];
      // Beat `.flowchart--animating-* { transition: transform }` so dropping the
      // inline translate cannot interpolate back to a stale --node-tx.
      el.style.transition = 'none';
      if (target && Number.isFinite(target.x) && Number.isFinite(target.y)) {
        el.style.setProperty('--node-tx', `${target.x}px`);
        el.style.setProperty('--node-ty', `${target.y}px`);
      }
    });
    onComplete();
    // Inline transform overrides stylesheet `transform: translate(var(--node-tx),…)`.
    // Flush React first so --node-* match ELK, then drop inline transform (see onComplete).
    document.querySelectorAll(LAYOUT_FLIP_NODE_SELECTOR).forEach((el) => {
      if (isBitmapSourceNode(el)) return;
      el.style.removeProperty('transform');
    });
    // Always run reveal / toolbar / blocker cleanup. A superseding layout bump (e.g. Tidy or
    // connection auto-layout racing ELK after a dependency change) makes onComplete return false
    // so React state is not overwritten — but we still hid edges for this pass; skipping cleanup
    // strands flowchart--animating, the blocker, edges at opacity 0, and the tidy spinner.
    onAfterInlineCleanup?.();
    requestAnimationFrame(() => {
      document.querySelectorAll(LAYOUT_FLIP_NODE_SELECTOR).forEach((el) => {
        if (isBitmapSourceNode(el)) return;
        if (el.style.transition === 'none') {
          el.style.removeProperty('transition');
        }
      });
    });
  }, durationMs + 50);
}

export function snapDomNodesToPositions(positions) {
  if (!positions || Object.keys(positions).length === 0) return;
  document.querySelectorAll(LAYOUT_FLIP_NODE_SELECTOR).forEach((el) => {
    if (isBitmapSourceNode(el)) return;
    const id = el.getAttribute('data-task-id');
    const pos = positions[id];
    if (!isFinitePosition(pos)) return;
    el.style.transition = '';
    el.style.setProperty('--node-tx', `${pos.x}px`);
    el.style.setProperty('--node-ty', `${pos.y}px`);
    el.style.removeProperty('transform');
  });
}
